import React from 'react';
import { Toolbar } from '@mui/x-data-grid';
import { GridToolbarExportMenu } from '../utils/gridToolbarParts.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FlagIcon from '@mui/icons-material/Flag';
import YearTabsSelector from '../../common/YearTabsSelector.js';
import MultiSelectFilter from './MultiSelectFilter.js';
import { getUserNameById } from '../../UserContext.js';
import logger from '../../../utils/logger';

const TaskToolbar = ({
  viewMode,
  activeCounty,
  activeMuni,
  activeUser,
  handleBack,
  stageFilter,
  setStageFilter,
  reviewTypeFilter,
  setReviewTypeFilter,
  yearFilter,
  setYearFilter,
  availableYears = [],
  selectedRows = [],
  groupingType,
  setGroupingType,
  myTasksToggle,
  setMyTasksToggle,
  onAssignClick,
}) => {
  // Get normalized stage filter (handle both string and array for backward compatibility)
  const normalizedStageFilter = Array.isArray(stageFilter) ? stageFilter[0] || 'all' : (stageFilter || 'all');

  const handleAssign = async () => {
    logger.log('[TaskGrid] TaskToolbar assign clicked', {
      viewMode,
      groupingType,
      selectedRowCount: selectedRows.length,
      selectedRows: selectedRows.map((row) => ({
        id: row.id,
        countyCode: row.countyCode,
        muniCode: row.muniCode,
        userId: row.userId,
        ParcelID: row.ParcelID || row.parcel_id,
        ReviewType: row.ReviewType,
      })),
    });
    if (onAssignClick) {
      await onAssignClick();
    }
  };

  const hasSelectedRows = selectedRows.length > 0;
  const stageOptions = React.useMemo(() => [
    { value: 'all', label: 'All' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'completed', label: 'Completed' },
  ], []);

  const reviewTypeOptions = React.useMemo(() => [
    { value: 'all', label: 'All' },
    { value: 'DataCleaning', label: 'Data Review' },
    { value: 'CaseReview', label: 'Case Review' },
  ], []);

  const groupingTypeOptions = React.useMemo(() => [
    { value: 'County', label: 'County' },
    { value: 'Municipality', label: 'Municipality' },
    { value: 'User', label: 'User' },
  ], []);

  const exportFileBaseName = React.useMemo(() => {
    const taxYear = yearFilter != null && yearFilter !== '' ? String(yearFilter) : 'year';
    const grouping = (groupingType || '')
      .trim()
      .replace(/\s+/g, '_')
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '') || 'group';
    return `${taxYear}_tasks_by_${grouping}`;
  }, [yearFilter, groupingType]);

  return (
    <Toolbar
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        pb: 0,
        minHeight: 48,
        height: 48,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        {viewMode === 'county' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              Task count grouped by
            </Typography>
            <Select
              value={groupingType}
              onChange={(e) => setGroupingType(e.target.value)}
              size="small"
              sx={{
                minWidth: 120,
                height: 28,
                '& .MuiSelect-select': {
                  py: 0.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'text.primary',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {groupingTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={myTasksToggle && option.value === 'User'}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={handleBack}
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {viewMode === 'municipality'
                ? activeCounty?.name 
                  ? `Task Counts by Municipality (${activeCounty.name})`
                  : activeUser?.id
                  ? `Task Counts by Municipality (User: ${activeUser.id === 'Unassigned' ? 'Unassigned' : getUserNameById(activeUser.id) || activeUser.name || 'Unknown'})`
                  : 'Task Counts by Municipality'
                : (() => {
                    const muniName = activeMuni?.name || '';
                    const filters = [];
                    if (activeUser?.id) {
                      const userName = activeUser.id === 'Unassigned' 
                        ? 'Unassigned' 
                        : getUserNameById(activeUser.id) || activeUser.name || 'Unknown';
                      filters.push(`User: ${userName}`);
                    }
                    return filters.length > 0 
                      ? `Tasks in ${muniName} (${filters.join(', ')})`
                      : `Tasks in ${muniName}`;
                  })()}
            </Typography>
          </Box>
        )}
        {/* Export button */}
        <GridToolbarExportMenu
          csvOptions={{ fileName: exportFileBaseName }}
          printOptions={{ fileName: exportFileBaseName }}
        />
      </Box>
      <Box sx={{ 
        position: 'absolute', 
        left: '50%', 
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        pt: 1,
        justifyContent: 'center'
      }}>
        <YearTabsSelector
          activeYear={yearFilter?.toString() || ''}
          availableYears={availableYears.map(y => y.toString())}
          onChange={(event, newYear) => {
            if (newYear) {
              setYearFilter(Number(newYear));
            }
          }}
        />
      </Box>
      <Box sx={{ flex: '0 0 auto', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {viewMode !== 'tasks' && (
          <>
            <ToggleButtonGroup
              value={myTasksToggle ? 'myTasks' : ''}
              exclusive
              onChange={(e, value) => setMyTasksToggle(value === 'myTasks')}
              size="small"
              color="primary"
              sx={{ height: 32 }}
            >
              <ToggleButton value="myTasks" aria-label="my tasks">
                My Tasks
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 } }}>
              <InputLabel>Stage</InputLabel>
              <Select
                value={normalizedStageFilter}
                label="Stage"
                onChange={(e) => setStageFilter(e.target.value)}
              >
                {stageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {viewMode === 'tasks' && (
          <MultiSelectFilter
            label="Review Type"
            value={reviewTypeFilter}
            onChange={setReviewTypeFilter}
            options={reviewTypeOptions}
            minWidth={140}
          />
        )}
        <Tooltip
          title={!hasSelectedRows ? 'Select rows to assign' : ''}
          disableHoverListener={hasSelectedRows}
        >
          <span>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAssign}
              disabled={!hasSelectedRows}
              endIcon={<FlagIcon fontSize="small" />}
              sx={{ minWidth: '100px', height: 32 }}
            >
              Assign
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Toolbar>
  );
};

export default TaskToolbar;

